import React from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Alert } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

/**
 * Investment Strategy Options
 */
const INVESTMENT_STRATEGIES = {
  'savings_account': {
    name: 'High-Yield Savings Account',
    description: 'FDIC insured, low risk, easy access',
    defaultRate: 4.5, // Current high-yield savings rates (2025)
    minRate: 3.0,
    maxRate: 6.0,
    risk: 'Very Low',
    liquidity: 'Immediate',
    examples: 'Ally Bank, Capital One 360, Marcus by Goldman Sachs'
  },
  'money_market': {
    name: 'Money Market Account',
    description: 'Similar to savings, slightly higher rates',
    defaultRate: 4.75,
    minRate: 3.5,
    maxRate: 6.5,
    risk: 'Very Low',
    liquidity: 'Immediate',
    examples: 'Fidelity, Vanguard, Schwab'
  },
  'cd_ladder': {
    name: 'CD Ladder',
    description: 'Certificates of Deposit with fixed terms',
    defaultRate: 5.0,
    minRate: 4.0,
    maxRate: 6.5,
    risk: 'Very Low',
    liquidity: 'Limited (term-based)',
    examples: '1-5 year CDs'
  },
  'conservative_portfolio': {
    name: 'Conservative Portfolio (30/70)',
    description: '30% stocks, 70% bonds - Lower risk, steady growth',
    defaultRate: 5.5,
    minRate: 4.0,
    maxRate: 7.0,
    risk: 'Low',
    liquidity: '1-3 days',
    examples: 'Target date funds, balanced funds'
  },
  'moderate_portfolio': {
    name: 'Moderate Portfolio (60/40)',
    description: '60% stocks, 40% bonds - Balanced risk and return',
    defaultRate: 7.0,
    minRate: 5.0,
    maxRate: 9.0,
    risk: 'Moderate',
    liquidity: '1-3 days',
    examples: 'S&P 500 index funds, total market funds'
  },
  'aggressive_portfolio': {
    name: 'Aggressive Portfolio (90/10)',
    description: '90% stocks, 10% bonds - Higher risk, higher potential return',
    defaultRate: 9.0,
    minRate: 7.0,
    maxRate: 12.0,
    risk: 'High',
    liquidity: '1-3 days',
    examples: 'S&P 500, growth stocks, tech stocks'
  },
  'stock_market': {
    name: 'Stock Market (100% Stocks)',
    description: '100% equities - Maximum growth potential, highest risk',
    defaultRate: 10.0,
    minRate: 8.0,
    maxRate: 15.0,
    risk: 'Very High',
    liquidity: '1-3 days',
    examples: 'S&P 500 index (historical ~10% average), individual stocks'
  },
  'mixed_strategy': {
    name: 'Mixed Strategy (Custom)',
    description: 'Custom allocation across multiple investment types',
    defaultRate: 7.5,
    minRate: 4.0,
    maxRate: 12.0,
    risk: 'Variable',
    liquidity: 'Variable',
    examples: 'Combination of savings, bonds, and stocks'
  }
};

/**
 * Investment Strategy Selector Component
 * 📊 Helps users select investment strategy and adjust interest rates accordingly
 */
const InvestmentStrategySelector = ({
  selectedStrategy = 'savings_account',
  interestRate = 4.5,
  onStrategyChange,
  onInterestRateChange
}) => {
  const strategy = INVESTMENT_STRATEGIES[selectedStrategy] || INVESTMENT_STRATEGIES['savings_account'];
  
  // Update interest rate when strategy changes
  React.useEffect(() => {
    if (onInterestRateChange && strategy.defaultRate !== interestRate) {
      onInterestRateChange(strategy.defaultRate);
    }
  }, [selectedStrategy]); // Only run when strategy changes

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Investment Strategy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select your investment strategy to automatically set realistic interest rates
      </Typography>

      {/* Strategy Selector */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Investment Strategy</InputLabel>
        <Select
          value={selectedStrategy}
          label="Investment Strategy"
          onChange={(e) => {
            const newStrategy = e.target.value;
            onStrategyChange(newStrategy);
            // Auto-update interest rate to strategy default
            if (onInterestRateChange) {
              onInterestRateChange(INVESTMENT_STRATEGIES[newStrategy].defaultRate);
            }
          }}
        >
          {Object.entries(INVESTMENT_STRATEGIES).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              <Box>
                <Typography variant="body1">{value.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {value.description} • {value.defaultRate}% default rate
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Strategy Details */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {strategy.name}
        </Typography>
        <Typography variant="caption" display="block">
          <strong>Risk Level:</strong> {strategy.risk} | <strong>Liquidity:</strong> {strategy.liquidity}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          <strong>Examples:</strong> {strategy.examples}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          <strong>Rate Range:</strong> {strategy.minRate}% - {strategy.maxRate}% (Default: {strategy.defaultRate}%)
        </Typography>
      </Alert>
    </Box>
  );
};

export default InvestmentStrategySelector;

